/**
 * Bundle helpers for multi-file / multi-sheet exports.
 *
 *   - `buildWorkbookBytes`  : many tables → one XLSX workbook (a sheet per table,
 *                             plus an info sheet). Returns raw bytes so the
 *                             workbook can be downloaded directly OR embedded in
 *                             a ZIP.
 *   - `buildCsv`            : one table → CSV text (UTF-8 + BOM, RFC-4180).
 *   - `downloadZip`         : a list of named entries → a single .zip download.
 *
 * SheetJS and JSZip are both dynamic-imported so they stay out of the main
 * bundle (mirrors `data-explorer/lib/export.ts`).
 */
import type { TableMatrix } from '@/features/data-explorer/lib/export'

// ─── download primitive ──────────────────────────────────────────────────────

export function triggerDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ─── CSV ──────────────────────────────────────────────────────────────────────

function csvCell(v: string): string {
  if (/[",\n\r]/.test(v)) return `"${v.replace(/"/g, '""')}"`
  return v
}

export function buildCsv(headers: string[], matrix: string[][]): string {
  const lines = [headers, ...matrix].map((row) => row.map(csvCell).join(','))
  return '﻿' + lines.join('\r\n') // BOM so Excel reads UTF-8
}

// ─── XLSX (multi-sheet workbook) ──────────────────────────────────────────────

export interface NamedTable extends TableMatrix {
  /** Desired sheet name (will be sanitized + de-duplicated). */
  sheetName: string
}

export interface WorkbookMeta {
  title: string
  generatedAt: string
}

/**
 * Excel sheet names: ≤ 31 chars, none of `[]:*?/\`, non-empty, unique within
 * the workbook. Sanitize → truncate → de-dup with `_2`, `_3`, … (same scheme as
 * `dedupeFormKey`).
 */
export function sanitizeSheetName(raw: string, taken: Set<string>): string {
  const name = (raw || 'Sheet').replace(/[[\]:*?/\\]/g, '_').slice(0, 31).trim() || 'Sheet'
  if (!taken.has(name)) {
    taken.add(name)
    return name
  }
  let n = 2
  // Keep room for the `_n` suffix within the 31-char budget.
  while (true) {
    const suffix = `_${n}`
    const candidate = name.slice(0, 31 - suffix.length) + suffix
    if (!taken.has(candidate)) {
      taken.add(candidate)
      return candidate
    }
    n++
  }
}

export async function buildWorkbookBytes(
  tables: NamedTable[],
  meta: WorkbookMeta,
): Promise<Uint8Array> {
  const XLSX = await import('xlsx')
  const wb = XLSX.utils.book_new()

  const infoRows = [
    ['عنوان', meta.title],
    ['تاریخ خروجی', meta.generatedAt],
    ['تعداد برگه', String(tables.length)],
  ]
  XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(infoRows), 'اطلاعات')

  const taken = new Set<string>(['اطلاعات'])
  for (const t of tables) {
    const ws = XLSX.utils.aoa_to_sheet([t.headers, ...t.matrix])
    XLSX.utils.book_append_sheet(wb, ws, sanitizeSheetName(t.sheetName, taken))
  }

  // `array` → ArrayBuffer-backed Uint8Array (downloadable + zip-embeddable).
  return XLSX.write(wb, { type: 'array', bookType: 'xlsx' }) as Uint8Array
}

// ─── ZIP ────────────────────────────────────────────────────────────────────

export interface ZipEntry {
  /** Path within the archive, e.g. `data/diabetes.csv`. */
  name: string
  content: string | Uint8Array
}

export async function downloadZip(filename: string, entries: ZipEntry[]) {
  const JSZip = (await import('jszip')).default
  const zip = new JSZip()
  for (const e of entries) zip.file(e.name, e.content)
  const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE' })
  triggerDownload(blob, filename)
}
